/**
 * SettingsPanel - Creates and manages the settings panel UI
 * Handles speech settings, UI preferences, and user interactions
 */
import DOMHelper from "./DOMHelper.js";
import { t } from "../core/i18n.js";

export default class SettingsPanel {
	/** @type {Object} Default settings values */
	#defaultSettings = {
		highlightColor: "#ffeb3b",
		bannerColor: "#2196f3",
		showBanner: false,
		showHighlight: true,
	};

	/** @type {HTMLElement|null} Settings panel DOM element */
	#element = null;

	/** @type {Object} Current UI settings */
	#settings = {};

	/** @type {Function|null} Callback for setting changes */
	#onSettingChange = null;

	/** @type {Function|null} Callback when panel should be hidden */
	#onHide = null;

	/** @type {boolean} Whether the panel is currently visible */
	#isVisible = false;

	/** @type {Function|null} Outside click handler for closing panel */
	#outsideClickHandler = null;

	/** @type {Object} Speech settings loaded from localStorage */
	#speechSettings = {};
	/**
	 * Initialize SettingsPanel
	 * @param {Object} settings - UI settings object
	 * @param {Function} onSettingChange - Callback for setting changes
	 * @param {Function} onHide - Callback when panel should be hidden
	 */
	constructor(settings = {}, onSettingChange = null, onHide = null) {
		// Merge provided settings with defaults
		this.#settings = { ...this.#defaultSettings, ...settings };
		this.#onSettingChange = onSettingChange;
		this.#onHide = onHide;

		// Load speech settings from localStorage
		this.#speechSettings = this.#loadSpeechSettings();
	}

	/**
	 * Create the settings panel element
	 * @returns {HTMLElement} The created panel element
	 */
	create() {
		this.#element = DOMHelper.createElement(
			"div",
			"tjs-settings-panel tjs-hidden",
		);

		// Create title and settings using helper methods
		const title = DOMHelper.createElement(
			"h3",
			"tjs-settings-title",
			t("settings.title"),
		);

		const speedSetting = DOMHelper.createLabeledInput(
			t("settings.speed"),
			"range",
			"tjs-speed-slider",
			{ min: "0.1", max: "3", step: "0.1", value: "1" },
			"tjs-speed-value",
			`1.0${t("settings.speedValueSuffix")}`,
		);

		const pitchSetting = DOMHelper.createLabeledInput(
			t("settings.pitch"),
			"range",
			"tjs-pitch-slider",
			{ min: "0", max: "2", step: "0.1", value: "1" },
			"tjs-pitch-value",
			"1.0",
		);

		const bannerSetting = DOMHelper.createCheckboxColor(
			t("settings.banner"),
			"tjs-banner-checkbox",
			"tjs-banner-color",
			"#2196f3",
		);

		const highlightSetting = DOMHelper.createCheckboxColor(
			t("settings.highlight"),
			"tjs-highlight-checkbox",
			"tjs-highlight-color",
			"#ffeb3b",
		);

		const shortcutsSection = this.#createShortcutsSection();

		// Create content container with all settings
		const contentDiv = DOMHelper.createContainer("tjs-settings-content", [
			title,
			speedSetting,
			pitchSetting,
			bannerSetting,
			highlightSetting,
			shortcutsSection,
		]);

		this.#element.appendChild(contentDiv);
		this.#addEventListeners();
		this.#loadSettingsIntoUI();

		return this.#element;
	}

	/**
	 * Create a slider setting group
	 * @param {string} labelText - Label text
	 * @param {string} sliderClass - CSS class for slider
	 * @param {string} inputType - Input type
	 * @param {Object} attributes - Input attributes
	 * @param {string} valueClass - CSS class for value display
	 * @param {string} initialValue - Initial value text
	 * @returns {HTMLElement} Setting group element
	 */
	createSliderSetting(
		labelText,
		sliderClass,
		inputType,
		attributes,
		valueClass,
		initialValue,
	) {
		const group = document.createElement("div");
		group.className = "tjs-setting-group";

		const label = document.createElement("label");
		label.className = "tjs-label";
		label.textContent = labelText;
		group.appendChild(label);

		const slider = document.createElement("input");
		slider.type = inputType;
		slider.className = `tjs-slider ${sliderClass}`;
		Object.assign(slider, attributes);
		group.appendChild(slider);

		const valueSpan = document.createElement("span");
		valueSpan.className = `tjs-value ${valueClass}`;
		valueSpan.textContent = initialValue;
		group.appendChild(valueSpan);

		return group;
	}

	/**
	 * Create a checkbox with color picker setting group
	 * @param {string} labelText - Label text
	 * @param {string} checkboxClass - CSS class for checkbox
	 * @param {string} colorClass - CSS class for color picker
	 * @param {string} defaultColor - Default color value
	 * @returns {HTMLElement} Setting group element
	 */
	createCheckboxColorSetting(
		labelText,
		checkboxClass,
		colorClass,
		defaultColor,
	) {
		const group = document.createElement("div");
		group.className = "tjs-setting-group";

		const label = document.createElement("label");
		label.className = "tjs-checkbox-label";

		const checkbox = document.createElement("input");
		checkbox.type = "checkbox";
		checkbox.className = checkboxClass;
		label.appendChild(checkbox);

		const checkmark = document.createElement("span");
		checkmark.className = "tjs-checkmark";
		label.appendChild(checkmark);

		label.appendChild(document.createTextNode(labelText));
		group.appendChild(label);

		const colorPicker = document.createElement("input");
		colorPicker.type = "color";
		colorPicker.className = `tjs-color-picker ${colorClass}`;
		colorPicker.value = defaultColor;
		group.appendChild(colorPicker);

		return group;
	}

	/**
	 * Create the keyboard shortcuts section
	 * @returns {HTMLElement} Shortcuts section element
	 */
	#createShortcutsSection() {
		const title = DOMHelper.createElement(
			"h4",
			null,
			t("settings.shortcuts.title"),
		);

		const shortcutList = [
			{ keys: "Alt + P", action: t("settings.shortcuts.playPause") },
			{ keys: "Alt + V", action: t("settings.shortcuts.next") },
			{ keys: "Alt + Z", action: t("settings.shortcuts.previous") },
		];

		const shortcutElements = shortcutList.map((shortcut) => {
			const kbd = DOMHelper.createElement("kbd", null, shortcut.keys);
			const shortcutDiv = DOMHelper.createElement("div", "tjs-shortcut");
			shortcutDiv.appendChild(kbd);
			shortcutDiv.appendChild(
				document.createTextNode(` ${shortcut.action}`),
			);
			return shortcutDiv;
		});

		return DOMHelper.createContainer("tjs-shortcuts", [
			title,
			...shortcutElements,
		]);
	}

	/**
	 * Add event listeners to settings panel controls
	 */
	#addEventListeners() {
		if (!this.#element) return;

		const speedSlider = this.#element.querySelector(".tjs-speed-slider");
		const pitchSlider = this.#element.querySelector(".tjs-pitch-slider");
		const bannerCheckbox = this.#element.querySelector(
			".tjs-banner-checkbox",
		);
		const highlightCheckbox = this.#element.querySelector(
			".tjs-highlight-checkbox",
		);
		const bannerColor = this.#element.querySelector(".tjs-banner-color");
		const highlightColor = this.#element.querySelector(
			".tjs-highlight-color",
		);

		// Speed slider
		speedSlider?.addEventListener("input", (e) => {
			const value = parseFloat(e.target.value);
			this.#element.querySelector(".tjs-speed-value").textContent =
				`${value.toFixed(1)}x`;
			this.#onSettingChange("speech", { rate: value });
		});

		// Pitch slider
		pitchSlider?.addEventListener("input", (e) => {
			const value = parseFloat(e.target.value);
			this.#element.querySelector(".tjs-pitch-value").textContent =
				value.toFixed(1);
			this.#onSettingChange("speech", { pitch: value });
		});

		// Banner checkbox
		bannerCheckbox?.addEventListener("change", (e) => {
			this.#settings.showBanner = e.target.checked;
			this.#onSettingChange("ui", { showBanner: e.target.checked });
		});

		// Highlight checkbox
		highlightCheckbox?.addEventListener("change", (e) => {
			this.#settings.showHighlight = e.target.checked;
			this.#onSettingChange("ui", { showHighlight: e.target.checked });
		});

		// Color pickers
		bannerColor?.addEventListener("change", (e) => {
			this.#settings.bannerColor = e.target.value;
			this.#onSettingChange("ui", { bannerColor: e.target.value });
		});

		highlightColor?.addEventListener("change", (e) => {
			this.#settings.highlightColor = e.target.value;
			this.#onSettingChange("ui", { highlightColor: e.target.value });
		});

		// Outside click handler
		this.#outsideClickHandler = (e) => {
			if (
				this.#isVisible &&
				!this.#element.contains(e.target) &&
				!e.target.closest(".tjs-settings-btn")
			) {
				this.hide();
			}
		};
	}

	/**
	 * Load current settings into UI elements
	 */
	#loadSettingsIntoUI() {
		if (!this.#element) return;

		// Load speech settings
		const speedSlider = this.#element.querySelector(".tjs-speed-slider");
		const pitchSlider = this.#element.querySelector(".tjs-pitch-slider");
		const speedValue = this.#element.querySelector(".tjs-speed-value");
		const pitchValue = this.#element.querySelector(".tjs-pitch-value");

		if (speedSlider && this.#speechSettings.rate !== undefined) {
			speedSlider.value = this.#speechSettings.rate;
			if (speedValue)
				speedValue.textContent = `${this.#speechSettings.rate.toFixed(1)}x`;
		}

		if (pitchSlider && this.#speechSettings.pitch !== undefined) {
			pitchSlider.value = this.#speechSettings.pitch;
			if (pitchValue)
				pitchValue.textContent = this.#speechSettings.pitch.toFixed(1);
		}

		// Load UI settings
		const bannerCheckbox = this.#element.querySelector(
			".tjs-banner-checkbox",
		);
		const highlightCheckbox = this.#element.querySelector(
			".tjs-highlight-checkbox",
		);
		const bannerColor = this.#element.querySelector(".tjs-banner-color");
		const highlightColor = this.#element.querySelector(
			".tjs-highlight-color",
		);

		if (bannerCheckbox) bannerCheckbox.checked = this.#settings.showBanner;
		if (highlightCheckbox)
			highlightCheckbox.checked = this.#settings.showHighlight;
		if (bannerColor) bannerColor.value = this.#settings.bannerColor;
		if (highlightColor)
			highlightColor.value = this.#settings.highlightColor;
	}

	/**
	 * Show the settings panel
	 */
	show() {
		if (!this.#element || this.#isVisible) return;

		this.#element.classList.remove("tjs-hidden");
		this.#isVisible = true;

		// Remove any existing listener before adding new one to prevent duplicates
		if (this.#outsideClickHandler) {
			document.removeEventListener("click", this.#outsideClickHandler);
		}

		// Add outside click listener
		document.addEventListener("click", this.#outsideClickHandler);
	}

	/**
	 * Hide the settings panel
	 */
	hide() {
		if (!this.#element || !this.#isVisible) return;

		this.#element.classList.add("tjs-hidden");
		this.#isVisible = false;

		// Remove outside click listener
		if (this.#outsideClickHandler) {
			document.removeEventListener("click", this.#outsideClickHandler);
		}

		if (this.#onHide) {
			this.#onHide();
		}
	}

	/**
	 * Toggle panel visibility
	 */
	toggle() {
		if (this.#isVisible) {
			this.hide();
		} else {
			this.show();
		}
	}

	/**
	 * Update settings and refresh UI
	 * @param {Object} newSettings - New settings to apply
	 */
	updateSettings(newSettings) {
		if (newSettings.ui) {
			this.#settings = { ...this.#settings, ...newSettings.ui };
		}
		if (newSettings.speech) {
			this.#speechSettings = {
				...this.#speechSettings,
				...newSettings.speech,
			};
		}

		// Reload UI to reflect changes
		this.loadSettingsIntoUI();
	}

	/**
	 * Remove the panel from DOM
	 */
	destroy() {
		// Remove outside click listener
		if (this.#outsideClickHandler) {
			document.removeEventListener("click", this.#outsideClickHandler);
		}

		if (this.#element) {
			this.#element.remove();
		}
		this.#element = null;
	}

	/**
	 * Load speech settings from localStorage
	 * @returns {Object} Speech settings object
	 */
	#loadSpeechSettings() {
		// Return defaults if localStorage is not available
		const defaults = {
			rate: 1.0,
			pitch: 1.0,
			volume: 1.0,
		};

		if (!this.isLocalStorageAvailable()) {
			return defaults;
		}

		try {
			const saved = localStorage.getItem("titusjs-speech-settings");
			if (saved) {
				const settings = JSON.parse(saved);
				// Validate that parsed data is an object and has expected properties
				if (
					typeof settings === "object" &&
					settings !== null &&
					!Array.isArray(settings)
				) {
					const validatedSettings = { ...defaults };
					// Only accept numeric values within valid ranges
					if (
						typeof settings.rate === "number" &&
						settings.rate >= 0.1 &&
						settings.rate <= 10
					) {
						validatedSettings.rate = settings.rate;
					}
					if (
						typeof settings.pitch === "number" &&
						settings.pitch >= 0 &&
						settings.pitch <= 2
					) {
						validatedSettings.pitch = settings.pitch;
					}
					if (
						typeof settings.volume === "number" &&
						settings.volume >= 0 &&
						settings.volume <= 1
					) {
						validatedSettings.volume = settings.volume;
					}
					return validatedSettings;
				}
			}
		} catch (error) {
			console.warn(
				"TitusJS: Could not load speech settings from localStorage:",
				error,
			);
		}

		return defaults;
	}

	/**
	 * Check if localStorage is available and working
	 * @returns {boolean} Whether localStorage is available
	 */
	isLocalStorageAvailable() {
		try {
			const test = "__tjs_storage_test__";
			localStorage.setItem(test, test);
			localStorage.removeItem(test);
			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Get the panel element
	 * @returns {HTMLElement|null} The panel element
	 */
	getElement() {
		return this.#element;
	}
}
